// src/context/persona/PersonaEditor.jsx
//
// The structured persona editor: one panel over GET|PUT
// /personas/{id}/sections. It also serves the CREATE flow (same fields, no initial GET),
// so a new persona cannot be written in a shape the editor could not reopen.
//
// v197: a save is ONE request. The PUT stamps the persona as hand-written server-side, but since BE c84cb07 no
// automatic process updates personas (the run's CUSTOMIZING stage is gone; only the operator CLI reads the
// stamp), so the editor no longer offers "let the AI keep updating it" and never calls unlock / lock.
//
// v197 — PROPOSAL MODE. `proposal = { job }` (a COMPLETED persona-assist job) opens the job's `proposal` — which
// is exactly this editor's PUT body — as the draft. A create proposal is an ordinary create (editable id
// pre-filled with `suggested_id`, saved create-only); a personalize proposal edits `persona_id` with
// `base_etag` as its If-Match and ⛔ makes NO initial read (the proposal IS the content to review). The proposal
// itself is the change, so Save is enabled at once. A personalize save refused with 412 CONTEXT_ETAG_MISMATCH
// means the persona moved since the job ran: the proposal is dropped, the persona is re-read into the ordinary
// editor, and the parent is told through `onProposalDropped`. The job's number warnings (ITALIAN paths) are
// shown beside their field on the Italian tab; a path the form does not show goes in a note above it.
//
// The ENGLISH half is ALL-OR-NOTHING. Every field empty means "no English
// version" and is sent as `en: null`; a half-filled one is refused here, before
// any request. ⚠️ An English half the GET returned is ALWAYS sent back, so
// editing the Italian tab and saving can never delete it.
//
// Headings and sub-labels come from the GET (`labels.it` / `labels.en`) — that
// copy belongs to the server, and the file's own language wins over the UI's. A
// create has no GET to read them from, hence DEFAULT_LABELS below.
//
// ⛔ Never render the server's sentence: a refused save is worded from
// `err.code` alone (field-by-field through `mapServerErrors`), and every
// message here is stored as an i18n KEY and translated at render, so it is
// never frozen in whatever language was active when the request failed.
//
// v198 — A MODAL BODY. The persona modal (PersonaModal) owns the title, the step bar and closing; this component
// owns the draft and therefore its own footer buttons, rendered through <ModalFooter> (in place when there is no
// modal, which keeps it testable alone). Three modes:
//   • 'edit'   — one persona; Salva · Ripristina (reset the draft) · Chiudi. The MARKET is READ-ONLY (decision:
//                it is chosen at creation and fixes the Pain & Gain format). Saving stays in the modal ("Salvato.").
//   • 'create' — the MANUAL WIZARD: identity (name, market, derived id under Avanzate) → profile (description,
//                language) → needs (role, context, Pain & Gain) → Salva (create-only). Each Avanti checks only its
//                step's fields; a refused save jumps back to the earliest step holding an error. IT only: no
//                English step — a manual save sends `en: null` and the backend writes the Italian file alone, so
//                the last step says how to add the English later.
//   • 'review' — an AI proposal (`proposal = { job }`), rules unchanged from v197. The market is READ-ONLY in both
//                kinds (the proposal was written for it); a create proposal keeps an editable id under Avanzate.
// BASE / AVANZATE (edit + review): the page preference `advanced` (useAdvancedView) decides; an error on a hidden
// field — or the 412 "id taken" — forces Avanzate open for THIS editor (not persisted), again on every NEW error
// list, and the toggle counts hidden errors + hidden AI number warnings while off.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { COLORS, SEMANTIC } from '../../constants/theme.js';
import { ModalFooter } from '../../components/ui/WizardModal.jsx';
import AdvancedToggle from '../../components/ui/AdvancedToggle.jsx';
import SegmentedTabs from '../../components/ui/SegmentedTabs.jsx';
import { getPersonaSections, putPersonaSections } from '../contextApi.js';
import { parseWarnings, proposalToDraft } from './assist/assistModel.js';
import DocResults from './assist/DocResults.jsx';
import CreateIdentityStep from './CreateIdentityStep.jsx';
import {
  PERSONA_ID_RE, buildBody, checkDraft, checkHalf, draftFromDetail, emptyHalf, earliestStep, errorsForStep,
  formatForMarket, hasAdvancedError, hiddenCount, isHalfEmpty, isNameTaken, mapServerErrors, uniquePersonaId,
} from './personaModel.js';
import PersonaHalfForm from './PersonaHalfForm.jsx';
import {
  alertBox, choiceHint, fieldLabel, fieldName, footerRow, inlineField, inlineFieldName, ghostBtn, primaryBtn,
  readOnlyValue, stepHeading, textInput, pairNoteStyle,
} from './personaStyles.js';

// The headings the backend serves today, needed only where there is no GET to
// read them from (a brand-new id). ⚠️ If the server's wording changes, the GET
// wins everywhere except the first screen of a create.
export const DEFAULT_LABELS = {
  it: {
    who: 'Chi è',
    pain_gain: 'Pain & Gain',
    language_use: 'Linguaggio: usa',
    language_avoid: 'Linguaggio: evita',
    role: "Ruolo nel processo d'acquisto",
    context: 'Contesto',
    pain: 'Pain',
    gain: 'Gain',
  },
  en: {
    who: 'Who they are',
    pain_gain: 'Pain & Gain',
    language_use: 'Language: use',
    language_avoid: 'Language: avoid',
    role: 'Role in the buying process',
    context: 'Context',
    pain: 'Pain',
    gain: 'Gain',
  },
};

const clone = (x) => JSON.parse(JSON.stringify(x));

// The markets the selects offer, in display order (v197 adds `any`).
const MARKETS = ['b2b', 'any', 'b2c'];
// v201: how many create-only saves the wizard tries, each with the next free id, before a plain error.
const WIZARD_ID_ATTEMPTS = 3;

// A create starts from the market's shape with the typed name already in place:
// the name is the one thing the user has said so far, and retyping it would be
// the first thing the form asked for.
function createDraft({ name = '', market = 'b2b' } = {}) {
  const format = formatForMarket(market);
  return { market, it: { ...emptyHalf(format), name }, en: emptyHalf(format) };
}

// The Italian paths PersonaHalfForm draws for this half — a warning on any OTHER path has no field to sit beside.
function shownPaths(half) {
  const paths = ['name', 'who.summary', 'who.role', 'who.context', 'language_use', 'language_avoid'];
  const pg = half && half.pain_gain;
  if (pg && pg.format === 'list') {
    (pg.items || []).forEach((_, n) => {
      ['pain', 'gain', 'indicators'].forEach((k) => paths.push(`pain_gain.items.${n}.${k}`));
    });
  } else {
    paths.push('pain_gain.text');
  }
  return new Set(paths);
}

export default function PersonaEditor({
  tenantId, personaId, canWrite, mode = 'edit', proposal = null, existingIds = [], existingNames = [], createInit = null,
  advanced = false, onAdvancedChange, onStepChange, onDirtyChange, onClose, onSaved, onProposalDropped,
}) {
  const { t } = useTranslation(['context', 'common']);
  // Read ONCE: the job a review was opened with does not change under it.
  const [fromJob] = useState(() => (mode === 'review' && proposal && proposal.job && proposal.job.proposal ? proposal.job : null));
  const wizard = mode === 'create';
  // A create (wizard or AI create proposal): editable id, create-only save, no initial GET.
  const createMode = wizard || Boolean(fromJob && fromJob.kind === 'create');
  // True while the draft is still the AI's proposal: until it is saved, or dropped by a 412.
  const [proposalLive, setProposalLive] = useState(Boolean(fromJob));
  const [step, setStep] = useState(wizard ? 'identity' : null);
  // v201: ids a create-only save of THIS wizard was refused for (taken outside the listing); never shown.
  const [refusedIds, setRefusedIds] = useState([]);
  // THIS editor's override of the page preference (an error on a hidden field); never persisted.
  const [forcedOpen, setForcedOpen] = useState(false);

  const [lang, setLang] = useState('it');
  // v197: in create mode the id is EDITABLE (a create save is create-only, so a taken id answers 412
  // PERSONA_ALREADY_EXISTS and the user picks another one with the form kept). For an edit it never changes.
  const [currentId, setCurrentId] = useState(() => {
    if (fromJob) return (createMode ? fromJob.suggested_id : fromJob.persona_id) || personaId || '';
    if (wizard) return '';   // v201: the wizard's id is derived at save time (`putWithFreeId`), never kept here
    return personaId;
  });
  const [draft, setDraft] = useState(() => {
    if (fromJob) return proposalToDraft(fromJob.proposal);
    return wizard ? createDraft(createInit || {}) : null;
  });
  // In proposal mode there is NO baseline: the proposal itself is the change, so Save is enabled at once.
  const [baseline, setBaseline] = useState(() => (!fromJob && wizard ? createDraft(createInit || {}) : null));
  const [labels, setLabels] = useState(DEFAULT_LABELS);
  const [etag, setEtag] = useState(() => (fromJob && !createMode ? fromJob.base_etag || null : null));
  const [hadEn, setHadEn] = useState(() => Boolean(fromJob && fromJob.proposal.en));
  const [loading, setLoading] = useState(!wizard && !fromJob);
  const [saving, setSaving] = useState(false);
  const [legacy, setLegacy] = useState(false);
  // Every message is { key, params } — see the header note on render-time copy.
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [general, setGeneral] = useState([]);
  const [notices, setNotices] = useState([]);
  const [ok, setOk] = useState(null);

  const applyDetail = useCallback((data) => {
    const next = draftFromDetail(data);
    setDraft(next);
    setBaseline(clone(next));
    setHadEn(Boolean(data.en));
    setEtag(data.etag || null);
    if (data.labels && data.labels.it && data.labels.en) setLabels(data.labels);
  }, []);

  // ⚠️ `keepMessages` exists for the re-read that follows a save: the server
  // normalises the text, so the fields must be refreshed, but the notice about
  // what just happened is the whole point and must survive it.
  const load = useCallback(async ({ keepMessages = false } = {}) => {
    setLoading(true);
    if (!keepMessages) {
      setError(null); setFieldErrors({}); setGeneral([]); setNotices([]); setOk(null);
      setLegacy(false);
    }
    try {
      const data = await getPersonaSections({ tenant: tenantId, id: currentId });
      applyDetail(data);
      setLegacy(false);
    } catch (e) {
      setDraft(null);
      // The file predates the four-section model: there is nothing this form
      // could open without rewriting it, so it says so and draws no fields.
      if (e.code === 'PERSONA_LEGACY_FORMAT') setLegacy(true);
      else setError({ key: 'personas.errors.generic', params: { code: e.code || e.status || '—' } });
    } finally {
      setLoading(false);
    }
  }, [tenantId, currentId, applyDetail]);

  // ⛔ A proposal makes no initial read: its content is the proposal, and a personalize one carries its etag.
  // Neither does the wizard: there is nothing on the server yet.
  useEffect(() => { if (!wizard && !fromJob) load(); }, [wizard, fromJob, load]);

  const warnings = useMemo(() => (fromJob && proposalLive ? parseWarnings(fromJob.warnings) : {}), [fromJob, proposalLive]);
  const strayWarnings = useMemo(() => {
    const shown = draft ? shownPaths(draft.it) : new Set();
    return Object.entries(warnings).filter(([path]) => !shown.has(path));
  }, [warnings, draft]);

  const body = useMemo(() => (draft ? buildBody(draft, { hadEn }) : null), [draft, hadEn]);
  const baseBody = useMemo(() => (baseline ? buildBody(baseline, { hadEn }) : null), [baseline, hadEn]);
  const textDirty = Boolean(body) && JSON.stringify(body) !== JSON.stringify(baseBody);
  // The language tabs' unsaved-changes dot: a half differs from the loaded one (none without a baseline).
  const halfDirty = (code) => Boolean(body && baseBody) && JSON.stringify(body[code]) !== JSON.stringify(baseBody[code]);
  const langTabs = ['it', 'en'].map((code) => ({
    key: code, label: t(`personas.editor.lang.${code}`), dirty: halfDirty(code),
  }));
  // v201: the wizard's id is derived at save time (`putWithFreeId`): the name's slug ("persona" for a name with no
  // usable letters), or the first free "-2", "-3"… against the listing and the refused ids. Always valid, never shown.
  const idOk = wizard || !createMode || PERSONA_ID_RE.test(currentId);
  // v201: what the wizard refuses is a NAME already used (the id never collides: it moves to the next free one).
  // Instant feedback only; the create-only save is the authority on ids.
  const nameTaken = wizard && Boolean(draft) && isNameTaken(draft.it.name, existingNames);
  const canSave = Boolean(canWrite && draft && !loading && !saving && (textDirty || createMode) && idOk && !nameTaken);
  const dirty = textDirty || Boolean(fromJob && proposalLive);
  const idProblem = Boolean(error && error.key === 'personas.create.idTaken');
  const advancedShown = advanced || forcedOpen;
  const hidden = hiddenCount({ errors: fieldErrors, warnings, lang });

  useEffect(() => { if (onDirtyChange) onDirtyChange(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => { if (wizard && onStepChange) onStepChange(step); }, [wizard, step, onStepChange]);
  // Force Avanzate open on every NEW error list that names a hidden field (each failed save builds a new object),
  // or on the 412 "id taken" — never persisted, and closing it by hand sticks until the next new list. The wizard
  // shows the role, context and Pain & Gain on their own step, so there only the id needs the reveal.
  useEffect(() => {
    if ((!wizard && hasAdvancedError(fieldErrors)) || idProblem) setForcedOpen(true);
  }, [wizard, fieldErrors, idProblem]);

  function toggleAdvanced(next) {
    setForcedOpen(false);
    if (onAdvancedChange) onAdvancedChange(next);
  }

  async function applyError(e) {
    // ⛔ FIRST: a create-only save refused because the id exists. It is also a 412, and the etag branch below
    // would reload — there is nothing to reload on a create, and the user's text must stay. (v201: only an AI
    // create review gets here — the wizard retries with the next free id itself, `putWithFreeId`.)
    if (e.code === 'PERSONA_ALREADY_EXISTS') {
      setError({ key: 'personas.create.idTaken' });
      return;
    }
    // A personalize proposal whose persona changed since the job read it: the proposal is dropped and this
    // becomes the ordinary editor of the persona as it is now.
    if (proposalLive && !createMode && (e.code === 'CONTEXT_ETAG_MISMATCH' || e.status === 412)) {
      setProposalLive(false);
      setBaseline(null);
      await load({ keepMessages: true });
      setError({ key: 'personas.assist.changedMeanwhile' });
      onProposalDropped?.();
      return;
    }
    if (e.code === 'BRIEF_VALIDATION_ERROR' || e.code === 'CONTEXT_ENTITY_INVALID') {
      const mapped = mapServerErrors(e);
      setFieldErrors(mapped.fields);
      setGeneral(mapped.general);
      setError({ key: 'personas.errors.headline' });
      showTabFor(Object.keys(mapped.fields));
      if (wizard) { const s = earliestStep(Object.keys(mapped.fields)); if (s) setStep(s); }
    } else if (e.code === 'PERSONA_FORMAT_MISMATCH') {
      await load({ keepMessages: true });
      setError({ key: 'personas.errors.formatMismatch' });
    } else if (e.code === 'CONTEXT_ETAG_MISMATCH' || e.status === 412) {
      await load({ keepMessages: true });
      setError({ key: 'config.etagNotice' });
    } else if (e.code === 'PERSONA_LEGACY_FORMAT') {
      setError({ key: 'personas.errors.legacy' });
    } else if (e.code === 'RUN_ACTIVE') {
      setError({ key: 'personas.errors.runActive' });
    } else if (e.code === 'SERVICE_UNAVAILABLE' || e.status === 503) {
      setError({ key: 'personas.errors.serviceDown' });
    } else {
      setError({ key: 'personas.errors.generic', params: { code: e.code || e.status || '—' } });
    }
  }

  // A refused field the user cannot see is a dead end: when every complaint is
  // about the other half, open that tab.
  function showTabFor(keys) {
    if (!keys.length) return;
    if (keys.every(k => k.startsWith('en.'))) setLang('en');
    else if (keys.every(k => k.startsWith('it.'))) setLang('it');
  }

  async function handleSave() {
    if (!draft) return;
    const clientErrs = checkDraft(draft);
    if (Object.keys(clientErrs).length) {
      const keys = Object.keys(clientErrs);
      const partial = !isHalfEmpty(draft.en) && keys.some(k => k.startsWith('en.'));
      setFieldErrors(clientErrs); setGeneral([]); setNotices([]); setOk(null);
      setError({ key: partial ? 'personas.errors.enPartial' : 'personas.errors.headline' });
      showTabFor(keys);
      if (wizard) { const s = earliestStep(keys); if (s) setStep(s); }
      return;
    }

    setSaving(true);
    setError(null); setFieldErrors({}); setGeneral([]); setNotices([]); setOk(null);
    const payload = buildBody(draft, { hadEn });
    try {
      const notes = [];
      const { id, res } = await putWithFreeId(payload);
      if (!id) return;
      if (res && res.strict_mode_activated) notes.push({ key: 'personas.editor.strictModeActivated' });
      // After an edit, re-GET: the server normalises the text and the PUT returns no fields.
      // After a CREATE the modal closes and the list reloads; a later Modifica reads the new id itself.
      if (!createMode) await load({ keepMessages: true });
      setProposalLive(false);
      setNotices(notes);
      setOk({ key: res && res.created ? 'personas.editor.created' : 'personas.editor.saved' });
      onSaved?.(id, { name: ((draft.it && draft.it.name) || '').trim() });
    } catch (e) {
      await applyError(e);
    } finally {
      setSaving(false);
    }
  }

  // The PUT. v201: the wizard's id is never shown, so an id refused as taken (outside the listing, or created
  // meanwhile) is replaced by the next free one and the save retried — every attempt create-only, so nothing is ever
  // overwritten. After WIZARD_ID_ATTEMPTS refusals: a plain error, `{ id: null }`. Anything else throws.
  async function putWithFreeId(payload) {
    if (!wizard) {
      const res = await putPersonaSections({
        tenant: tenantId, id: currentId, body: payload, etag: createMode ? null : etag, ifNoneMatch: createMode,
      });
      return { id: currentId, res };
    }
    const refused = [...refusedIds];
    for (let attempt = 1; attempt <= WIZARD_ID_ATTEMPTS; attempt += 1) {
      const id = uniquePersonaId(draft.it.name.trim(), [...existingIds, ...refused]);
      try {
        // eslint-disable-next-line no-await-in-loop -- sequential by design: each retry needs the previous refusal
        const res = await putPersonaSections({ tenant: tenantId, id, body: payload, etag: null, ifNoneMatch: true });
        return { id, res };
      } catch (e) {
        if (e.code !== 'PERSONA_ALREADY_EXISTS') throw e;
        refused.push(id);
      }
    }
    setRefusedIds(refused);
    setError({ key: 'personas.create.idExhausted' });
    return { id: null, res: null };
  }

  function chooseMarket(market) {
    setDraft((prev) => {
      if (!prev) return prev;
      // ⛔ On an existing persona the market is a label only: the Pain & Gain
      // shape was fixed by the first write and the server refuses a mismatch.
      // (Backstop: outside a create the market is shown read-only and never reaches here.)
      if (!createMode) return { ...prev, market };
      // Same Pain & Gain shape (e.g. b2b → any): keep what is written — an AI proposal's rows included.
      if (formatForMarket(market) === formatForMarket(prev.market)) return { ...prev, market };
      const shape = emptyHalf(formatForMarket(market)).pain_gain;
      return { market, it: { ...prev.it, pain_gain: clone(shape) }, en: { ...prev.en, pain_gain: clone(shape) } };
    });
  }

  function resetDraft() {
    if (!baseline) return;
    setDraft(clone(baseline));
    setError(null); setFieldErrors({}); setGeneral([]); setNotices([]); setOk(null);
  }

  // ── The manual wizard ─────────────────────────────────────────────────────
  // Avanti checks only the fields its step shows; the save checks everything (checkDraft above).
  function nextStep() {
    if (step === 'identity') {
      if (!draft.it.name.trim() || !idOk || nameTaken) return;
      setFieldErrors({}); setError(null); setStep('profile');
      return;
    }
    if (step === 'profile') {
      // The description and the two language fields (the name was checked on the previous step).
      const errs = errorsForStep(checkHalf(draft.it, 'it'), 'profile');
      if (Object.keys(errs).length) { setFieldErrors(errs); setError({ key: 'personas.errors.headline' }); return; }
      setFieldErrors({}); setError(null); setStep('needs');
    }
  }

  function prevStep() {
    setError(null); setFieldErrors({});
    setStep(step === 'needs' ? 'profile' : 'identity');
  }

  function setName(name) {
    setDraft((prev) => ({ ...prev, it: { ...prev.it, name } }));
  }

  const setItHalf = (h) => setDraft((prev) => ({ ...prev, it: h }));
  const nextBlocked = !draft || !draft.it.name.trim() || !idOk || nameTaken;
  const saveLabel = saving ? t('config.saving') : t('common:actions.save');

  // READ-ONLY everywhere outside the manual wizard (whose first step is where a manual create picks it): in Modifica
  // and in BOTH reviews. An AI proposal was written for the market chosen in its brief — switching between a list
  // market (B2B / Qualsiasi) and B2C changes the Pain & Gain format and would EMPTY the Pain & Gain the AI wrote.
  // Label and value on ONE row (inlineField), aligned with the Nome row below it. A value the picker does not
  // know is shown raw.
  const marketField = draft && (
    <div style={{ ...inlineField, marginBottom: '14px' }}>
      <span style={inlineFieldName}>{t('personas.fields.market')}</span>
      <p style={{ ...readOnlyValue, margin: 0 }} title={t('personas.editor.marketReadOnly')}>
        {MARKETS.includes(draft.market) ? t(`personas.markets.${draft.market}`) : (draft.market || '—')}
      </p>
    </div>
  );

  return (
    <div>
      {fromJob && proposalLive && (
        <div style={alertBox(SEMANTIC.info.fg)}>
          <div>{t('personas.assist.proposalNote')}</div>
          {fromJob.kb_used === false && <div style={{ marginTop: '6px' }}>{t('personas.assist.kbUnused')}</div>}
          <DocResults documents={fromJob.documents} />
        </div>
      )}
      {strayWarnings.length > 0 && (
        <div role="note" style={alertBox(SEMANTIC.attenzione.fg)}>
          <div style={{ fontWeight: 600 }}>{t('personas.assist.warningsTitle')}</div>
          <ul style={{ margin: '4px 0 0', paddingLeft: '18px' }}>
            {strayWarnings.map(([path, numbers]) => (
              <li key={path}>{t('personas.assist.warningField', { numbers: numbers.join(', ') })}</li>
            ))}
          </ul>
        </div>
      )}
      {legacy && (
        <div role="alert" style={alertBox(SEMANTIC.attenzione.fg)}>{t('personas.errors.legacy')}</div>
      )}
      {error && (
        <div role="alert" style={alertBox(SEMANTIC.errore.fg)}>{t(error.key, error.params)}</div>
      )}
      {general.length > 0 && (
        <ul style={{ ...alertBox(SEMANTIC.errore.fg), listStyle: 'disc', paddingLeft: '32px' }}>
          {general.map((reason, i) => <li key={i}>{t(`personas.errors.${reason}`)}</li>)}
        </ul>
      )}
      {notices.map((n, i) => (
        <div key={i} style={alertBox(SEMANTIC.attenzione.fg)}>{t(n.key)}</div>
      ))}
      {ok && <div style={alertBox(SEMANTIC.verde.fg)}>{t(ok.key)}</div>}
      {loading && <p style={choiceHint}>{t('config.loading')}</p>}

      {draft && !legacy && wizard && (
        <>
          {step === 'identity' && (
            <>
              {/* v201: no id field and no Avanzate here — the id is always derived (`putWithFreeId`). */}
              <CreateIdentityStep
                name={draft.it.name} market={draft.market} taken={nameTaken} disabled={saving}
                onName={setName} onMarket={chooseMarket}
              />
            </>
          )}
          {step === 'profile' && (
            <>
              <h3 style={stepHeading}>{t('personas.wizard.profileHeading')}</h3>
              <PersonaHalfForm
                half={draft.it} lang="it" labels={labels.it || DEFAULT_LABELS.it}
                errors={fieldErrors} readOnly={false} show={{ name: false, advanced: false }} onChange={setItHalf}
              />
            </>
          )}
          {step === 'needs' && (
            <>
              <h3 style={stepHeading}>{t('personas.wizard.needsHeading')}</h3>
              <PersonaHalfForm
                half={draft.it} lang="it" labels={labels.it || DEFAULT_LABELS.it}
                errors={fieldErrors} readOnly={false} show={{ name: false, summary: false, language: false }}
                onChange={setItHalf}
              />
              <p style={{ ...pairNoteStyle, marginTop: '16px' }}>{t('personas.wizard.enNote')}</p>
            </>
          )}
        </>
      )}

      {draft && !legacy && !wizard && (
        <>
          {/* The hint sits OUTSIDE the <label>: inside it would become part of the input's accessible name. */}
          {createMode && advancedShown && (
            <>
              <label style={{ ...fieldLabel, marginBottom: '6px' }}>
                <span style={fieldName}>{t('personas.create.id')}</span>
                <input
                  type="text" value={currentId} disabled={saving} aria-invalid={!PERSONA_ID_RE.test(currentId)}
                  onChange={(e) => { setCurrentId(e.target.value.trim().toLowerCase()); if (idProblem) setError(null); }}
                  style={{ ...textInput, maxWidth: '360px' }}
                />
              </label>
              <p style={{ ...pairNoteStyle, marginBottom: '14px' }}>{t('personas.create.idHint')}</p>
            </>
          )}
          {/* Outside the tabs on purpose: the market is one property of the persona, not of a language half. */}
          {marketField}

          {/* The Italiano / English switch: the SAME tabs as Brand Voice's voice languages (SegmentedTabs, pill
              look, arrow keys, a dot on a language with unsaved changes). Everything below it is the ONE panel
              the two tabs control. */}
          {/* A plain block, not a flex row: the tabs then span the full width, half each, as in Brand Voice. */}
          <div style={{ margin: '20px 0 8px' }}>
            <SegmentedTabs
              tabs={langTabs} value={lang} onChange={setLang}
              label={t('personas.editor.languages')} idPrefix="persona-lang"
              dirtyLabel={t('personas.editor.unsavedLang')}
            />
          </div>

          <div role="tabpanel" id="persona-lang-panel" aria-labelledby={`persona-lang-${lang}`}>
            {lang === 'en' && isHalfEmpty(draft.en) && (
              <div style={alertBox(SEMANTIC.info.fg)}>{t('personas.editorV2.enEmpty')}</div>
            )}

            {/* BASE first — name, "Chi è" (description), Linguaggio — then the toggle, then what it reveals:
                role and context (the rest of "Chi è") and Pain & Gain. Two renders of the same half, each showing
                its part, so the advanced fields always appear BELOW the toggle that opens them. */}
            <PersonaHalfForm
              half={draft[lang]} lang={lang} labels={labels[lang] || DEFAULT_LABELS[lang]}
              errors={fieldErrors} warnings={lang === 'it' ? warnings : {}} readOnly={!canWrite}
              show={{ advanced: false }}
              onChange={h => setDraft(prev => ({ ...prev, [lang]: h }))}
            />

            <div style={{ margin: '24px 0 0' }}>
              <AdvancedToggle
                on={advancedShown} onChange={toggleAdvanced} label={t('personas.advanced.label')}
                hiddenCount={hidden} hiddenLabel={t('personas.advanced.hidden', { count: hidden })}
              />
            </div>

            {advancedShown && (
              <PersonaHalfForm
                half={draft[lang]} lang={lang} labels={labels[lang] || DEFAULT_LABELS[lang]}
                errors={fieldErrors} warnings={lang === 'it' ? warnings : {}} readOnly={!canWrite}
                show={{ name: false, summary: false, language: false }}
                onChange={h => setDraft(prev => ({ ...prev, [lang]: h }))}
              />
            )}
          </div>
        </>
      )}

      {/* Rendered even without a draft (a failed load): "Chiudi" must always be there. */}
      <ModalFooter>
        {wizard ? (
          <div style={footerRow}>
            {step !== 'identity' && (
              <button type="button" disabled={saving} onClick={prevStep} style={ghostBtn(saving)}>{t('personas.wizard.back')}</button>
            )}
            <span style={{ flex: 1 }} />
            {step !== 'needs' ? (
              <button type="button" onClick={nextStep} disabled={nextBlocked} style={primaryBtn(nextBlocked)}>
                {t('personas.wizard.next')}
              </button>
            ) : (
              <button type="button" disabled={!canSave} onClick={handleSave} style={primaryBtn(!canSave)}>{saveLabel}</button>
            )}
          </div>
        ) : (
          <div style={footerRow}>
            {canWrite && (
              <button type="button" disabled={!canSave} onClick={handleSave} style={primaryBtn(!canSave)}>{saveLabel}</button>
            )}
            {canWrite && mode === 'edit' && (
              <button
                type="button" disabled={saving || !textDirty || !baseline} onClick={resetDraft}
                style={ghostBtn(saving || !textDirty || !baseline)}
              >
                {t('personas.editor.restore')}
              </button>
            )}
            <span style={{ marginLeft: 'auto', fontSize: '12px', color: COLORS.textSecondary }}>
              {canWrite ? (textDirty ? t('config.dirty') : t('config.clean')) : null}
            </span>
            <button type="button" onClick={onClose} style={ghostBtn(false)}>{t('personas.closeEditor')}</button>
          </div>
        )}
      </ModalFooter>
    </div>
  );
}
